import { Fragment, useMemo, useState } from "react";
import {
    Box,
    CircularProgress,
    Dialog,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    useTheme,
} from "@mui/material";
import {
    Button,
    FoodsIcon,
    FoodsIconButton,
    FoodsPagination,
    FoodsSelect,
    FoodsTags,
    Typography,
} from "@foodpilot/foods";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";

import {
    Campaign,
    useCreateCampaign,
    useDeleteCampaign,
    useGetAllCampaigns,
    useSetDefaultCampaign,
    useUpdateCampaign,
} from "src/api/client-api/useCampaign";
import { useCheckCampaignPosts } from "src/api/client-api/campaigns/useCheckCampaignPosts";
import { Loading } from "src/components/utils/Loading";
import { CAMPAIGN, CREATE, MODIFY, useFoodpilotContext } from "src/context/FoodpilotContext.tsx";
import { getApiViolations } from "src/utils/apiViolations";
import { useSnackbar } from "src/utils/useSnackbar";
import {
    CampaignDraft,
    CampaignDraftError,
    CampaignDraftErrors,
    CampaignDraftField,
    CampaignSort,
    CampaignSortKey,
    emptyCampaignDraft,
    resolveDefaultCampaign,
    sortCampaigns,
    toCampaignDraft,
    toCampaignUpdate,
    toCampaignWrite,
    validateCampaignDraft,
} from "./campaignRows";
import { SettingsError } from "./SettingsError";
import { useNavigateSettingsParent } from "./useNavigateSettingsParent";

const CAMPAIGNS_PER_PAGE = 10;

export const Campaigns = () => {
    const { canUser } = useFoodpilotContext();
    const { t } = useTranslation();
    const theme = useTheme();
    const queryClient = useQueryClient();
    const snackbar = useSnackbar();
    const navigateSettingsParent = useNavigateSettingsParent();

    const { campaigns, isLoading, isError } = useGetAllCampaigns({ orderByDate: true });
    const saveCampaign = useCreateCampaign();
    const updateCampaign = useUpdateCampaign();
    const deleteCampaign = useDeleteCampaign();
    const setDefaultCampaign = useSetDefaultCampaign();
    const checkCampaignPosts = useCheckCampaignPosts();

    const [sort, setSort] = useState<CampaignSort>({ key: "year", direction: "asc" });
    const [draft, setDraft] = useState<CampaignDraft | null>(null);
    const [draftErrors, setDraftErrors] = useState<CampaignDraftErrors>({});
    const [idToDelete, setIdToDelete] = useState<number | undefined>(undefined);
    const [displayDialog, setDisplayDialog] = useState(false);
    const [page, setPage] = useState(1);

    const sortedCampaigns = useMemo(() => sortCampaigns(campaigns, sort), [campaigns, sort]);

    const pageCount = Math.max(1, Math.ceil(sortedCampaigns.length / CAMPAIGNS_PER_PAGE));
    const currentPage = Math.min(page, pageCount);
    const visibleCampaigns = sortedCampaigns.slice(
        (currentPage - 1) * CAMPAIGNS_PER_PAGE,
        currentPage * CAMPAIGNS_PER_PAGE,
    );

    if (isLoading) return <Loading />;

    if (isError) {
        return (
            <SettingsError
                heading={t("campaigns", "Campaigns")}
                message={t(
                    "campaign.error.loadFailed",
                    "Failed to load campaigns. Please try refreshing the page or contact support if the problem persists.",
                )}
            />
        );
    }

    const defaultCampaign = resolveDefaultCampaign(campaigns);
    const isSaving = saveCampaign.isLoading || updateCampaign.isLoading;

    const errorMessages: Record<CampaignDraftError, string> = {
        required: t("settings.campaigns.error.required", "Required"),
        invalidYear: t("settings.campaigns.error.invalid_year", "Enter a four-digit year"),
        endBeforeStart: t("settings.campaigns.error.end_before_start", "The end date must follow the start date"),
    };

    const refreshCampaigns = () => queryClient.invalidateQueries(["getAllCampaigns"]);

    const closeDraft = () => {
        setDraft(null);
        setDraftErrors({});
    };

    const openCreateDraft = () => {
        setDraft(emptyCampaignDraft());
        setDraftErrors({});
    };

    const openEditDraft = (campaign: Campaign) => {
        setDraft(toCampaignDraft(campaign));
        setDraftErrors({});
    };

    const updateDraft = (field: CampaignDraftField, value: string) => {
        setDraft((current) => (current === null ? current : { ...current, [field]: value }));
        setDraftErrors((current) => ({ ...current, [field]: undefined }));
    };

    const submitDraft = () => {
        if (draft === null) return;

        const errors = validateCampaignDraft(draft);
        if (Object.keys(errors).length > 0) {
            setDraftErrors(errors);
            return;
        }

        const isCreation = draft.id === undefined;

        const handlers = {
            onSuccess: () => {
                refreshCampaigns();
                closeDraft();
                snackbar.setSnackbarProps({
                    message:
                        isCreation ?
                            t("settings.campaigns.create_success", "Campaign created successfully")
                        :   t("settings.campaigns.update_success", "Campaign updated successfully"),
                    type: "success",
                });
            },
            onError: (error: unknown) => {
                const violations = getApiViolations(error);
                const message =
                    violations
                        ?.map((violation) => violation.message)
                        .filter(Boolean)
                        .join(" ") ?? "";

                snackbar.setSnackbarProps({
                    message: message || t("campaign.error.saveFailed", "Failed to save campaign. Please try again."),
                    type: "error",
                });
            },
        };

        if (isCreation) {
            saveCampaign.mutate(toCampaignWrite(draft, false), handlers);
            return;
        }

        updateCampaign.mutate(toCampaignUpdate(draft), handlers);
    };

    const canChangeDefault = canUser(MODIFY, CAMPAIGN);
    const selectableCampaigns = sortCampaigns(
        campaigns.filter((campaign) => campaign.canModify !== false),
        { key: "year", direction: "asc" },
    );

    const campaignOptionName = (campaign: Campaign) =>
        campaign.label === String(campaign.year) ? campaign.label : `${campaign.year} - ${campaign.label}`;

    const selectTextOptions = {
        placeholder: t("component.select.placeholder"),
        searchPlaceholder: t("component.select.searchplaceholder"),
        emptyOptionsText: t("component.select.nooptions"),
        defaultValueText: t("component.select.defaultvalue"),
        noResult: t("component.select.noresultfound"),
    };

    const changeDefaultCampaign = (campaign: Campaign | null) => {
        if (campaign === null || campaign.id === defaultCampaign?.id) return;
        if (canChangeDefault === false || campaign.canModify === false || setDefaultCampaign.isLoading) return;

        setDefaultCampaign.mutate(campaign.id, {
            onSuccess: () => {
                refreshCampaigns();
                snackbar.setSnackbarProps({
                    message: t("settings.campaigns.default_success", "Default campaign updated"),
                    type: "success",
                });
            },
            onError: () => {
                snackbar.setSnackbarProps({
                    message: t("settings.campaigns.default_error", "Failed to update the default campaign"),
                    type: "error",
                });
            },
        });
    };

    const handleDeleteClick = (id: number) => {
        if (defaultCampaign?.id === id) {
            snackbar.setSnackbarProps({
                message: t(
                    "settings.campaigns.cannot_delete_default",
                    "Select another default campaign before deleting this one.",
                ),
                type: "warning",
            });
            return;
        }

        setIdToDelete(id);

        checkCampaignPosts.mutate(id, {
            onSuccess: (result) => {
                if (result.hasPosts) {
                    snackbar.setSnackbarProps({
                        message: t(
                            "campaign.error.cannotDeleteWithPosts",
                            `Cannot delete this campaign because it has ${result.totalPosts} post(s) associated with it. Please remove the posts from this campaign before deleting.`,
                        ),
                        type: "warning",
                    });
                } else {
                    setDisplayDialog(true);
                }
            },
            onError: () => {
                setDisplayDialog(true);
            },
        });
    };

    const confirmDelete = () => {
        if (idToDelete !== undefined) {
            deleteCampaign.mutate(idToDelete, {
                onSuccess: () => {
                    refreshCampaigns();
                    snackbar.setSnackbarProps({
                        message: t("campaign.deleteSuccess", "Campaign deleted successfully"),
                        type: "success",
                    });
                },
                onError: () => {
                    snackbar.setSnackbarProps({
                        message: t(
                            "campaign.error.cannotDeleteWithPosts",
                            "Cannot delete this campaign because it has posts associated with it. Please remove the posts from this campaign before deleting.",
                        ),
                        type: "error",
                    });
                },
            });
        }

        setDisplayDialog(false);
    };

    const toggleSort = (key: CampaignSortKey) => {
        if (draft !== null) return;

        setPage(1);
        setSort((current) =>
            current.key === key ?
                { key, direction: current.direction === "asc" ? "desc" : "asc" }
            :   { key, direction: "asc" },
        );
    };

    const sortIcon = (key: CampaignSortKey) => {
        if (sort.key !== key) return "sortNoSorting" as const;
        return sort.direction === "asc" ? ("sortAscending" as const) : ("sortDescending" as const);
    };

    const columnHeader = (key: CampaignSortKey, label: string) => (
        <TableCell>
            <Box
                component="button"
                onClick={() => toggleSort(key)}
                aria-label={label}
                disabled={draft !== null}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: theme.spacing(1),
                    width: "100%",
                    border: "none",
                    padding: 0,
                    backgroundColor: "transparent",
                    cursor: draft === null ? "pointer" : "default",
                }}
            >
                <Typography variant="small-medium" sx={{ color: theme.custom.grey[2000] }}>
                    {label}
                </Typography>
                <FoodsIcon icon={sortIcon(key)} size={1} />
            </Box>
        </TableCell>
    );

    const draftField = (field: CampaignDraftField, type: "text" | "number" | "date") => {
        const error = draftErrors[field];

        return (
            <TextField
                type={type}
                value={draft === null ? "" : draft[field]}
                onChange={(event) => updateDraft(field, event.target.value)}
                error={error !== undefined}
                helperText={error === undefined ? undefined : errorMessages[error]}
                placeholder={field === "label" ? t("enter_campaign_name", "Enter campaign name") : undefined}
                size="small"
                fullWidth
            />
        );
    };

    const draftRow = (key: string) => (
        <TableRow key={key} sx={{ "& .MuiTableCell-root": { height: theme.spacing(8) } }}>
            <TableCell>{draftField("label", "text")}</TableCell>
            <TableCell>{draftField("year", "number")}</TableCell>
            <TableCell>{draftField("startDate", "date")}</TableCell>
            <TableCell>{draftField("endDate", "date")}</TableCell>
            <TableCell>
                <Stack direction="row" alignItems="center">
                    {isSaving ?
                        <CircularProgress size={20} />
                    :   <FoodsIconButton
                            size="s"
                            icon="save"
                            tooltip={{ text: t("save", "Save") }}
                            buttonProps={{ onClick: submitDraft }}
                        />
                    }
                    <FoodsIconButton
                        size="s"
                        icon="cancel"
                        tooltip={{ text: t("cancel", "Cancel") }}
                        buttonProps={{ onClick: closeDraft, disabled: isSaving }}
                    />
                </Stack>
            </TableCell>
        </TableRow>
    );

    return (
        <Box sx={{ padding: theme.spacing(4) }}>
            <Paper
                elevation={0}
                sx={{
                    border: `1px solid ${theme.custom.grey[500]}`,
                    borderRadius: theme.spacing(2),
                    overflow: "hidden",
                    boxShadow: theme.custom.shadows.grey,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: theme.spacing(2),
                        padding: theme.spacing(4),
                        borderBottom: `1px solid ${theme.custom.grey[500]}`,
                    }}
                >
                    <Box
                        onClick={navigateSettingsParent}
                        sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                    >
                        <FoodsIcon icon="arrowLeftShort" size={6} />
                    </Box>
                    <Typography variant="h3">{t("campaigns", "Campaigns")}</Typography>
                </Box>

                <Stack gap={theme.spacing(3)} sx={{ padding: theme.spacing(3, 4) }}>
                    <Typography variant="h5">{t("settings.campaigns.settings_title", "Campaign settings")}</Typography>

                    <Stack direction="row" gap={theme.spacing(4)} flexWrap="wrap">
                        <Stack gap={theme.spacing(1)}>
                            <Typography variant="small-medium">
                                {t("settings.campaigns.default_campaign", "Default campaign")}
                            </Typography>
                            <FoodsSelect
                                selectedOption={defaultCampaign ?? null}
                                options={selectableCampaigns}
                                onChange={changeDefaultCampaign}
                                getId={(campaign) => campaign.id}
                                getName={(campaign) => campaignOptionName(campaign)}
                                closeOnSelect
                                disableSearch
                                disabled={canChangeDefault === false || setDefaultCampaign.isLoading}
                                textOptions={selectTextOptions}
                                BoxProps={{ width: theme.spacing(30) }}
                            />
                        </Stack>
                    </Stack>
                </Stack>

                <Box sx={{ padding: theme.spacing(0, 4, 3) }}>
                    {canUser(CREATE, CAMPAIGN) && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                paddingBottom: theme.spacing(2),
                            }}
                        >
                            <Button
                                variant="primary"
                                startIcon={<FoodsIcon size={2} icon="plus" />}
                                disabled={draft !== null}
                                onClick={openCreateDraft}
                            >
                                {t("add_campaign", "Add a campaign")}
                            </Button>
                        </Box>
                    )}

                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                            border: `1px solid ${theme.custom.grey[500]}`,
                            borderRadius: theme.spacing(1),
                            overflow: "hidden",
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        backgroundColor: theme.custom.grey[200],
                                        "& .MuiTableCell-root": {
                                            borderBottom: `1px solid ${theme.custom.grey[500]}`,
                                            borderRight: `1px solid ${theme.custom.grey[500]}`,
                                            height: theme.spacing(6.5),
                                            "&:last-child": { borderRight: "none" },
                                        },
                                    }}
                                >
                                    {columnHeader("label", t("campaign.name", "Campaign name"))}
                                    {columnHeader("year", t("settings.campaigns.col_year", "Year"))}
                                    {columnHeader("startDate", t("startdate", "Start date"))}
                                    {columnHeader("endDate", t("enddate", "End date"))}
                                    <TableCell sx={{ width: theme.spacing(15) }}>
                                        <Typography variant="small-medium" sx={{ color: theme.custom.grey[2000] }}>
                                            {t("actions", "Actions")}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody
                                sx={{
                                    "& .MuiTableCell-root": {
                                        borderRight: `1px solid ${theme.custom.grey[500]}`,
                                        borderBottom: `1px solid ${theme.custom.grey[500]}`,
                                        "&:last-child": { borderRight: "none" },
                                    },
                                    "& .MuiTableRow-root:last-of-type .MuiTableCell-root": {
                                        borderBottom: "none",
                                    },
                                }}
                            >
                                {sortedCampaigns.length === 0 && draft === null && (
                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ textAlign: "center" }}>
                                            <Typography variant="small-medium" sx={{ color: theme.custom.grey[2000] }}>
                                                {t(
                                                    "campaign.noCampaigns",
                                                    "No campaigns available. Create your first campaign to get started.",
                                                )}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}

                                {visibleCampaigns.map((campaign) => (
                                    <Fragment key={campaign.id}>
                                        {draft?.id === campaign.id ?
                                            draftRow(`draft-${campaign.id}`)
                                        :   <TableRow
                                                sx={{
                                                    "& .MuiTableCell-root": { height: theme.spacing(8) },
                                                    "&:hover": { backgroundColor: theme.custom.grey[300] },
                                                }}
                                            >
                                                <TableCell>
                                                    <Stack direction="row" alignItems="center" gap={theme.spacing(1)}>
                                                        <Typography variant="small-medium">{campaign.label}</Typography>
                                                        {defaultCampaign?.id === campaign.id && (
                                                            <FoodsTags
                                                                variant="tags-green"
                                                                label={t("settings.campaigns.badge_default", "Default")}
                                                            />
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="small-medium">{campaign.year}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="small-medium">
                                                        {dayjs(campaign.startDate).format("DD/MM/YYYY")}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="small-medium">
                                                        {dayjs(campaign.endDate).format("DD/MM/YYYY")}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" alignItems="center">
                                                        {campaign.canModify && (
                                                            <FoodsIconButton
                                                                size="s"
                                                                icon="edit"
                                                                tooltip={{ text: t("common.action.edit", "Edit") }}
                                                                buttonProps={{
                                                                    disabled: draft !== null,
                                                                    onClick: () => openEditDraft(campaign),
                                                                }}
                                                            />
                                                        )}
                                                        {campaign.canDelete && (
                                                            <FoodsIconButton
                                                                size="s"
                                                                icon="delete"
                                                                tooltip={{ text: t("delete", "Delete") }}
                                                                buttonProps={{
                                                                    disabled: draft !== null,
                                                                    onClick: () => handleDeleteClick(campaign.id),
                                                                }}
                                                            />
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        }
                                    </Fragment>
                                ))}

                                {draft !== null && draft.id === undefined && draftRow("draft-new")}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {pageCount > 1 && (
                        <Box sx={{ display: "flex", justifyContent: "center", paddingTop: theme.spacing(3) }}>
                            <FoodsPagination
                                page={currentPage}
                                setPage={(value) => {
                                    if (draft === null) setPage(value);
                                }}
                                pages={Array.from({ length: pageCount }, (_, index) => ({
                                    label: String(index + 1),
                                }))}
                                PaginationProps={{ disabled: draft !== null }}
                            />
                        </Box>
                    )}
                </Box>
            </Paper>

            <Dialog
                open={displayDialog}
                onClose={() => setDisplayDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: theme.spacing(2), overflow: "hidden" } }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: theme.spacing(2),
                        padding: theme.spacing(4),
                        borderBottom: `1px solid ${theme.custom.grey[500]}`,
                    }}
                >
                    <Stack gap={theme.spacing(1)}>
                        <Typography variant="h3">{t("campaign.confirmDelete", "Confirm Delete")}</Typography>
                        <Typography variant="small-medium" sx={{ color: theme.custom.grey[2500] }}>
                            {t("campaign.deleteDescription", "This action cannot be undone.")}
                        </Typography>
                    </Stack>
                    <FoodsIconButton
                        size="s"
                        icon="close"
                        tooltip={{ text: t("cancel", "Cancel") }}
                        buttonProps={{ onClick: () => setDisplayDialog(false) }}
                    />
                </Box>

                <Box sx={{ padding: theme.spacing(4), backgroundColor: theme.custom.grey[200] }}>
                    <Typography variant="small-medium" sx={{ color: theme.custom.grey[2800] }}>
                        {t(
                            "campaign.deleteWarning",
                            "Are you sure you want to delete this campaign? This action cannot be undone.",
                        )}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: theme.spacing(1.5),
                        padding: theme.spacing(2, 3),
                        borderTop: `1px solid ${theme.custom.grey[500]}`,
                    }}
                >
                    <Button
                        variant="secondary"
                        onClick={() => setDisplayDialog(false)}
                        disabled={deleteCampaign.isLoading}
                    >
                        {t("cancel", "Cancel")}
                    </Button>
                    <Button variant="delete" onClick={confirmDelete} disabled={deleteCampaign.isLoading}>
                        {deleteCampaign.isLoading ?
                            <CircularProgress size={20} color="inherit" />
                        :   t("delete", "Delete")}
                    </Button>
                </Box>
            </Dialog>
        </Box>
    );
};
